'use client';

import Script from 'next/script';
import { FreeTierBanner } from 'src/components/ProductDetailed/FreeTierBanner';
import { StatisticsBanner } from 'src/components/Restyling/ConstructorBlocks/StatisticsBanner';
import { useIntersectionObserveForms } from 'src/hooks/useIntersectionObserverForms';
import { Layout } from 'src/layout';
import { ProductForm, ProductProps } from 'src/types/backendContent';
import { Form } from 'src/uikit/Form';
import { ButtonProps, HeroInnerPage } from 'src/uikit/HeroInnerPage';
import { scrollToElementWithId } from 'src/utils/scrollToElementWithId';

import { PageBlocks } from '../../../components/PageBlocks';
import { Anchor } from '../../../components/ProductDetailed/AnchorMenu';
import { BlockTypes, ProductsList } from '../../../types/contentBlocks';
import { Breadcrumb } from '../../../uikit/Breadcrumbs';
import { Variant } from '../../../uikit/Button/constants';
import { ANCHOR_SERVICES, STATISTICS } from './mock';
import s from './Service.module.scss';

export function ServicePage({
  service,
  anchors,
  form,
  breadcrumbs = [],
}: {
  service: ProductProps;
  anchors: Anchor[];
  form: ProductForm;
  breadcrumbs?: Breadcrumb[];
}) {
  const { formRef, setAnchorId } = useIntersectionObserveForms();

  const getHeroButtons = () => {
    const heroButtons: ButtonProps[] = [];

    const onButtonClickToForm = () => {
      setAnchorId(`service_hero_anchor_to_form`);
      scrollToElementWithId('form');
    };

    const primaryButton = {
      title: 'Оставить заявку',
      onClick: () => onButtonClickToForm(),
      disabled: !form.isShow,
      analyticsOptions: {
        action: 'click',
        clickZone: 'body',
        clickElement: 'button',
        clickContent: 'Оставить заявку',
        uniqueId: `button-scroll-to-form-${service.title}`,
        transitionType: 'inside-link',
      },
    } satisfies ButtonProps;
    heroButtons.push(primaryButton);

    const foundedProductsList = service.content.blocks.find(
      ({ type }) => type === BlockTypes.ProductsList,
    ) as ProductsList | undefined;

    if (foundedProductsList) {
      const onButtonClickToServices = () => {
        scrollToElementWithId(
          foundedProductsList.data.id
            ? foundedProductsList.data.id
            : ANCHOR_SERVICES,
        );
      };

      const secondaryButton = {
        title: 'Выбор сервиса',
        onClick: () => onButtonClickToServices(),
        variant: Variant.Tertiary,
        analyticsOptions: {
          action: 'click',
          clickZone: 'body',
          clickElement: 'button',
          clickContent: 'Выбор сервиса',
          uniqueId: `button-scroll-to-service-${service.title}`,
          transitionType: 'inside-link',
        },
      } satisfies ButtonProps;

      heroButtons.push(secondaryButton);
    }

    return heroButtons;
  };

  const curBreadcrumbs = [
    ...breadcrumbs,
    {
      title: service.title,
    },
  ];

  let multipleComment = undefined;
  if (form.commentsList && form.commentsList.length > 0) {
    multipleComment = {
      name: 'comment',
      options:
        form.commentsList?.map((comment) => ({
          value: comment,
          label: comment,
        })) || [],
      label: 'Выберите',
    };
  }

  const FAQ_DATA = service.content.blocks.find(
    (block) => block.type === 'Accordion',
  );

  let jsonFaq = null;

  if (FAQ_DATA) {
    const entitys = FAQ_DATA.data.cards.map((entity) => {
      return {
        '@type': 'Question',
        name: entity.title,
        acceptedAnswer: {
          '@type': 'Answer',
          text: entity.richText,
        },
      };
    });

    jsonFaq = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [...entitys],
    };
  }
  return (
    <Layout>
      <div className={s.root}>
        <HeroInnerPage
          title={service.title}
          description={service.description}
          heroImage={service.icon3d || '/plugs/3d-model-default.png'}
          buttons={getHeroButtons()}
          anchorMenu={{ anchors: anchors, slug: service.slug }}
          breadcrumbs={{
            links: breadcrumbs[breadcrumbs.length - 1]?.link
              ? curBreadcrumbs
              : breadcrumbs,
          }}
        />

        {service.slug !== 'virtualnyj-server-gpu' ? <FreeTierBanner /> : null}

        <PageBlocks blocks={service.content.blocks} />

        <StatisticsBanner {...STATISTICS} />

        {form.isShow && (
          <Form
            id="form"
            ref={formRef}
            inputPath="v2/order"
            title={form.title}
            allFormsSentFormType="consultation"
            analyticsFieldFill={{ formType: 'body' }}
            successNotice={{ title: form.submitText || undefined }}
            multipleComment={multipleComment}
            buttonTitle={form.buttonTitle || undefined}
            formVariant="inner"
            successfulSubmissonFormAnalytics={{
              formname: 'lead-services',
            }}
          />
        )}
      </div>

      {jsonFaq && (
        <Script
          id="microfaq"
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonFaq) }}
        />
      )}
    </Layout>
  );
}
